import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { partnersService, activitiesService, mediaUrl } from '../lib/api-service';
import { DocumentMeta } from '../hooks/useDocumentMeta';
import type { PartnerDetail as PartnerDetailType, ActivityListItem } from '../lib/types';
import { formatDate, formatTime } from '../utils/date';
import { getTextColorHex } from '../utils/color-contrast';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Handshake, Phone, Mail, User, Loader2, Calendar, MapPin, Instagram } from 'lucide-react';
import { priceFromForActivity } from '../utils/participant-types';

const STATUS_LABELS: Record<string, string> = {
  public_entity: 'Instituție publică',
  private_company: 'Companie privată',
  ngo: 'ONG',
  informal_group: 'Grup informal',
};

export default function PartnerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<PartnerDetailType | null>(null);
  const [activities, setActivities] = useState<ActivityListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      partnersService.detail(slug),
      activitiesService.list({ partner: slug }),
    ])
      .then(([p, a]) => { setPartner(p); setActivities(a.results); })
      .catch(() => setError('Prietenul nu a fost găsit.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="mb-4 text-lg font-semibold">{error || 'Prieten negăsit'}</p>
        <button onClick={() => navigate('/parteneri')} className="text-sm font-medium text-[var(--vivid-purple)] hover:underline">
          Înapoi la prieteni
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <DocumentMeta
        title={partner.name}
        description={partner.description ? partner.description.slice(0, 160).replace(/[#*_]/g, '') : `${partner.name} — prieten Alba Iulia CTRo 2026`}
        image={partner.logo ? mediaUrl(partner.logo) : undefined}
        url={`/partener/${partner.slug}`}
      />
      {/* Back */}
      <button
        onClick={() => navigate('/parteneri')}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Înapoi la prieteni
      </button>

      {/* Header */}
      <div className="mb-6 flex items-center gap-5">
        {partner.logo ? (
          <img src={mediaUrl(partner.logo)} alt="" className="h-20 w-20 rounded-2xl border object-cover" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[var(--vivid-purple)]/10">
            <Handshake className="h-10 w-10 text-[var(--vivid-purple)]" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            {partner.name}
          </h1>
          <span
            className="mt-1 inline-block rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium text-muted-foreground"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            {STATUS_LABELS[partner.status] || partner.status}
          </span>
          {partner.legal_name && (
            <p className="mt-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              {partner.legal_name}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      {partner.description && (
        <div className="mb-8">
          <div className="prose prose-sm max-w-none" style={{ fontFamily: 'var(--font-secondary)' }}>
            <ReactMarkdown>{partner.description}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Contact card */}
      {(partner.contact_name || partner.contact_phone || partner.contact_email || partner.instagram) && (
        <section className="mb-8">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            <User className="h-5 w-5 text-[var(--vivid-purple)]" />
            Contact
          </h2>
          <div className="rounded-xl border bg-white p-4">
            {partner.contact_name && (
              <p className="mb-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                {partner.contact_name}
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              {partner.contact_phone && (
                <a href={`tel:${partner.contact_phone}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                  <Phone className="h-4 w-4 text-[var(--vivid-purple)]" />
                  {partner.contact_phone}
                </a>
              )}
              {partner.contact_email && (
                <a href={`mailto:${partner.contact_email}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                  <Mail className="h-4 w-4 text-[var(--vivid-purple)]" />
                  {partner.contact_email}
                </a>
              )}
              {partner.instagram && (
                <a
                  href={`https://instagram.com/${partner.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Instagram className="h-4 w-4 text-[var(--vivid-purple)]" />
                  @{partner.instagram}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Activities */}
      <section>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          <Calendar className="h-5 w-5 text-[var(--vivid-purple)]" />
          Activități ({activities.length})
        </h2>
        {activities.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Nicio activitate asociată.
          </p>
        ) : (
          <div className="space-y-3">
            {activities.map((a) => {
              const mainTag = a.tags.find((t) => t.is_main_tag);
              const priceFrom = priceFromForActivity(a.participant_types);
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(`/eveniment/${a.slug}`)}
                  className="flex w-full items-center gap-4 rounded-xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md active:scale-[0.99]"
                >
                  {a.banner_image ? (
                    <img
                      src={mediaUrl(a.banner_image)}
                      alt={a.title}
                      loading="lazy"
                      decoding="async"
                      className="h-16 w-24 flex-shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--vivid-purple)]/10 to-[var(--vivid-cyan)]/10">
                      <Calendar className="h-6 w-6 text-[var(--vivid-purple)]/40" />
                    </div>
                  )}
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-sm font-bold" style={{ fontFamily: 'var(--font-primary)' }}>{a.title}</h3>
                      {mainTag && (
                        <span
                          className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={{ backgroundColor: mainTag.color || '#3b0b86', color: getTextColorHex(mainTag.color || '#3b0b86') }}
                        >
                          {mainTag.name}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      <span>{formatDate(a.start_datetime)} · {formatTime(a.start_datetime)}</span>
                      {a.location_text && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {a.location_text}
                        </span>
                      )}
                      {priceFrom !== null && (
                        <span className="font-semibold text-[var(--vivid-purple)]">De la {priceFrom} RON</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
